import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { Body5, Body7, ConfirmModal, Container, TextField } from '@ui';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { showSuccessBanner } from '@utils/application';
import { REVIEW_ACTIONS, REVIEW_GROUP_STATUSES, REVIEW_SUBJECTS, decideReviewGroups, fetchRun } from '@services/fmdc';
import { CONTROL_HEIGHT } from '@features/scoreSpec/SegmentedControl';
import { DangerTransparentButton, HeaderCell, HeaderRow, Table, TableScroll } from './styles';
import { useUrlFilters } from '@hooks/application/useUrlFilters';
import { queueUrl, fmdcPaths } from './fmdcUrl';
import { useReviewGroups } from './useReviewGroups';
import { useSelection } from './useSelection';
import { SelectionBar } from './SelectionBar';
import { SelectionCheckbox } from './SelectionCheckbox';
import { Pager, clampOffset } from './Pager';
import { GateNotice } from './GateNotice';
import { NOTICE_TONES, OutcomePanel } from './NoticeText';
import { RerunConfirm } from './RerunConfirm';
import { ReviewFilters, isQueueFiltering } from './ReviewFilters';
import { REVIEW_GRID_COLUMNS, REVIEW_TABLE_MIN_WIDTH, ReviewGroupRow } from './ReviewGroupRow';
import { GroupCellsTable } from './GroupCellsTable';
import { pluralize } from './format';

// The review queue: one decision per spreadsheet row, about 500 groups per model, so paging and
// filters are the product. `GET /review_groups` lists both subjects in one shape; extraction groups
// are decided in bulk from here, unmapped groups open their own detail.
//
// The per-group `mapping_saved` / `mapping_note` on a decide is the demo's carry-over story, so it
// is shown beside the row's label in a panel that stays until dismissed — a banner alone would be
// gone before the reviewer read it.

// The reason input sits in a row with 40px buttons, so it takes the control height too.
const ReasonField = styled.div`
  width: 240px;

  input {
    height: ${CONTROL_HEIGHT};
    box-sizing: border-box;
    font-size: 13px;
  }
`;

const CONFIRMS = {
  [REVIEW_ACTIONS.REJECT]: {
    title: 'Reject these rows?',
    body: 'Rejected rows are dropped on the next rerun. The decision is saved for the issuer.',
    confirmLabel: 'Reject rows',
  },
  [REVIEW_ACTIONS.REQUEUE]: {
    title: 'Requeue these rows?',
    body: 'Requeue removes the saved decision for these rows and puts them back in the queue.',
    confirmLabel: 'Requeue rows',
  },
};

const VERBS = {
  [REVIEW_ACTIONS.APPROVE]: 'approved',
  [REVIEW_ACTIONS.REJECT]: 'rejected',
  [REVIEW_ACTIONS.REQUEUE]: 'requeued',
};

// The outcome notes a decide response leaves behind, one per group whose decision was not saved
// for the issuer. Labels are looked up from the groups as they were BEFORE the refetch — the row
// may have left the Open list by the time the note is read.
export const outcomeNotesFrom = (result, groupsById) =>
  (result?.results || [])
    .filter((entry) => entry.mapping_saved === false)
    .map((entry) => {
      const group = groupsById[entry.group_id];
      const label = group ? (group.row_label || '').trim() || `row ${group.row_idx}` : entry.group_id;
      const where = group ? `Row ${group.row_idx} '${label}'` : `Group ${entry.group_id}`;
      const note = entry.mapping_note ? `: ${entry.mapping_note}` : '.';
      return {
        key: entry.group_id,
        tone: NOTICE_TONES.WARNING,
        text: `${where}: decided, but not saved for the issuer${note}`,
      };
    });

export const ReviewQueueView = ({ runId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const { filters, setFilters } = useUrlFilters(queueUrl);
  const { groups, total, isLoading, error, reload } = useReviewGroups(runId, filters);

  const [run, setRun] = React.useState(null);
  const [expandedId, setExpandedId] = React.useState(null);
  const [reason, setReason] = React.useState('');
  const [confirmAction, setConfirmAction] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [decideError, setDecideError] = React.useState(null);
  const [outcomes, setOutcomes] = React.useState([]);
  const [rerunOpen, setRerunOpen] = React.useState(false);

  // The run supplies the filter vocabularies (reasons with counts, sheets) and the open count; a
  // failure here leaves the filters without options rather than blocking the queue.
  const refreshRun = React.useCallback(
    async (guard) => {
      const result = await fetchRun(runId);
      if (guard?.isCanceled) return;
      if (!result.error) setRun(result.run);
    },
    [runId]
  );

  React.useEffect(() => {
    const guard = { isCanceled: false };
    refreshRun(guard);
    return () => {
      guard.isCanceled = true;
    };
  }, [refreshRun]);

  const selectableIds = React.useMemo(
    () => groups.filter((g) => g.subject === REVIEW_SUBJECTS.EXTRACTION).map((g) => g.group_id),
    [groups]
  );
  const selection = useSelection(selectableIds);

  const backTo = `${location.pathname}${location.search}`;
  const filtering = isQueueFiltering(filters);

  const runDecide = async (action) => {
    if (busy || selection.count === 0) return;
    setBusy(true);
    setDecideError(null);
    const groupIds = selection.selectedIds;
    const groupsById = Object.fromEntries(groups.map((g) => [g.group_id, g]));

    const result = await decideReviewGroups({ runId, groupIds, action, reason: reason.trim() || undefined });
    setBusy(false);

    if (result.error) {
      setDecideError(result.error);
      return;
    }

    const decided = result.result?.groups ?? groupIds.length;
    showSuccessBanner(`${decided} ${pluralize(decided, 'row')} ${VERBS[action]}`, null, dispatch);

    const notes = outcomeNotesFrom(result.result, groupsById);
    if (notes.length) {
      setOutcomes((prev) => {
        const kept = prev.filter((item) => !notes.some((n) => n.key === item.key) && item.key !== 'set-issuer');
        const next = [...kept, ...notes];
        if (!run?.issuer_id) {
          next.push({
            key: 'set-issuer',
            tone: NOTICE_TONES.NEUTRAL,
            text: 'This run has no issuer, so no decision can be saved for one.',
            action: (
              <SkeletonButton key="set-issuer" onClick={() => setRerunOpen(true)}>
                Set an issuer and rerun
              </SkeletonButton>
            ),
          });
        }
        return next;
      });
    }

    selection.clear();
    setReason('');
    setExpandedId(null);

    // Re-fetch at the same offset; a page that emptied (decided rows left the Open list) is clamped
    // back onto the last page that still exists.
    const refreshed = await reload();
    // The open count and the per-reason counts come from the run, which a decide moves too.
    refreshRun();
    if (refreshed && !refreshed.error && filters.offset > 0 && refreshed.total <= filters.offset) {
      setFilters({ offset: clampOffset(filters.offset, refreshed.total) });
    }
  };

  const requestAction = (action) => {
    if (busy || selection.count === 0) return;
    if (CONFIRMS[action]) {
      setConfirmAction(action);
      return;
    }
    runDecide(action);
  };

  const confirmDecide = async () => {
    const action = confirmAction;
    await runDecide(action);
    setConfirmAction(null);
  };

  const toggleExpand = (groupId) => setExpandedId((current) => (current === groupId ? null : groupId));

  const goToRun = () => navigate(fmdcPaths.run(ROUTES.fmdc, runId));

  const clearFilters = () =>
    setFilters({
      subject: queueUrl.defaults.subject,
      status: queueUrl.defaults.status,
      reason: '',
      sheet: '',
      offset: 0,
    });

  const renderEmpty = () => {
    if (filtering) {
      return (
        <GateNotice compact title="No rows match these filters" body="Try widening the subject or removing a reason.">
          <TransparentFlatButton onClick={clearFilters}>Clear filters</TransparentFlatButton>
        </GateNotice>
      );
    }
    if (filters.status === REVIEW_GROUP_STATUSES.OPEN) {
      return (
        <GateNotice
          compact
          title="Nothing left to review on this run"
          body="Every row has a decision. Download the workbook or JSON from the run."
        >
          <SkeletonButton onClick={goToRun}>Back to run</SkeletonButton>
        </GateNotice>
      );
    }
    return <GateNotice compact title="No review items on this run" />;
  };

  return (
    <div>
      <ReviewFilters filters={filters} setFilters={setFilters} run={run} total={total} />

      {outcomes.length > 0 && (
        <Container top={16}>
          <OutcomePanel
            title="Decided, but not saved for the issuer"
            items={outcomes}
            onDismiss={() => setOutcomes([])}
          />
        </Container>
      )}

      <Container top={16}>
        <SelectionBar
          count={selection.count}
          noun="row"
          onClear={selection.clear}
          escapeClears={!confirmAction && !rerunOpen}
          error={decideError}
        >
          <ReasonField>
            <TextField
              value={reason}
              onChange={setReason}
              placeholder="Reason (optional)"
              aria-label="Reason for the decision"
            />
          </ReasonField>
          <FlatButton onClick={() => requestAction(REVIEW_ACTIONS.APPROVE)} disabled={busy}>
            {busy ? 'Working…' : 'Approve'}
          </FlatButton>
          <DangerTransparentButton onClick={() => requestAction(REVIEW_ACTIONS.REJECT)} disabled={busy}>
            Reject
          </DangerTransparentButton>
          <TransparentFlatButton onClick={() => requestAction(REVIEW_ACTIONS.REQUEUE)} disabled={busy}>
            Requeue
          </TransparentFlatButton>
        </SelectionBar>

        {isLoading ? (
          <Body5 isLowContrast>Loading review queue…</Body5>
        ) : error && groups.length === 0 ? (
          <div>
            <Body5>{error}</Body5>
            <Container top={12}>
              <SkeletonButton onClick={reload}>Retry</SkeletonButton>
            </Container>
          </div>
        ) : total === 0 ? (
          renderEmpty()
        ) : (
          <>
            {error && (
              <Container bottom={12}>
                <Body5>{error}</Body5>
              </Container>
            )}
            <TableScroll>
              <Table $minWidth={REVIEW_TABLE_MIN_WIDTH}>
                <HeaderRow $columns={REVIEW_GRID_COLUMNS}>
                  <div>
                    {selectableIds.length > 0 && (
                      <SelectionCheckbox
                        isChecked={selection.isPageFullySelected}
                        isIndeterminate={selection.isPagePartiallySelected}
                        onChange={(shouldSelect) => selection.setPage(shouldSelect)}
                        ariaLabel="Select all extraction rows on this page"
                      />
                    )}
                  </div>
                  <HeaderCell>Sheet · row</HeaderCell>
                  <HeaderCell>Row label</HeaderCell>
                  <HeaderCell>Item</HeaderCell>
                  <HeaderCell>Split · metric</HeaderCell>
                  <HeaderCell>Reasons</HeaderCell>
                  <HeaderCell>Confidence</HeaderCell>
                  <HeaderCell>Valid.</HeaderCell>
                  <HeaderCell>Cells</HeaderCell>
                  <HeaderCell>Sample values</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                  <span />
                </HeaderRow>

                {groups.map((group) => (
                  <React.Fragment key={group.group_id}>
                    <ReviewGroupRow
                      group={group}
                      runId={runId}
                      isSelected={selection.isSelected(group.group_id)}
                      onToggleSelect={selection.toggle}
                      isExpanded={expandedId === group.group_id}
                      onToggleExpand={toggleExpand}
                    />
                    {expandedId === group.group_id && group.subject === REVIEW_SUBJECTS.EXTRACTION && (
                      <GroupCellsTable runId={runId} group={group} backTo={backTo} />
                    )}
                  </React.Fragment>
                ))}
              </Table>
            </TableScroll>
            <Container top={8}>
              <Body7 isLowContrast>
                Unmapped rows are decided from their own page; select extraction rows to approve, reject or requeue them
                together.
              </Body7>
            </Container>
            <Pager
              offset={filters.offset}
              total={total}
              count={groups.length}
              onChange={(offset) => setFilters({ offset })}
            />
          </>
        )}
      </Container>

      {confirmAction && (
        <ConfirmModal
          title={CONFIRMS[confirmAction].title}
          body={CONFIRMS[confirmAction].body}
          confirmLabel={CONFIRMS[confirmAction].confirmLabel}
          busy={busy}
          onConfirm={confirmDecide}
          onCancel={() => setConfirmAction(null)}
        />
      )}

      {rerunOpen && run && (
        <RerunConfirm
          run={run}
          onDone={() => {
            setRerunOpen(false);
            goToRun();
          }}
          onCancel={() => setRerunOpen(false)}
        />
      )}
    </div>
  );
};
